import PropTypes from 'prop-types'
import { Button, Typography } from '@material-tailwind/react'
import { useState } from 'react'
import { FiCopy, FiCheck } from 'react-icons/fi'
import { NotificationModal } from '@/components/notification-modal'

const formatErrorDetail = (error) => {
  if (!error) return ''

  const message = error.message ? `Mensaje: ${error.message}` : ''
  const detail = error.error ? `Error: ${error.error}` : ''

  return [message, detail].filter(Boolean).join('\n')
}

export const MassiveImportResultModal = ({
  errors,
  iconType,
  message,
  onClose,
  title
}) => {
  const errorDetails = errors.map(formatErrorDetail).filter(Boolean).join('\n\n')
  const [copyStatus, setCopyStatus] = useState('idle')

  const clipboardText = [title, message, errorDetails].filter(Boolean).join('\n\n')

  const copyWithFallback = (text) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    textArea.style.pointerEvents = 'none'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)

    return copied
  }

  const handleCopy = async () => {
    if (!clipboardText) return

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText)
      } else if (!copyWithFallback(clipboardText)) {
        throw new Error('Clipboard API unavailable')
      }

      setCopyStatus('success')
    } catch (error) {
      console.error('Error copying massive import details:', error)
      const copied = copyWithFallback(clipboardText)
      setCopyStatus(copied ? 'success' : 'error')
    }

    setTimeout(() => setCopyStatus('idle'), 2500)
  }

  return (
    <NotificationModal
      title={title}
      message={message}
      iconType={iconType}
      setBackStateFunction={onClose}
      size='md'
      bodyClassName='max-h-[80vh] overflow-y-auto'
    >
      {errorDetails && (
        <div className='mt-4'>
          <div className='mb-3 flex items-center justify-between gap-3'>
            <Typography variant='small' className='text-gray-700'>
              Detalle de errores
            </Typography>
            <Button
              size='sm'
              variant='outlined'
              className='flex items-center gap-2 normal-case'
              onClick={handleCopy}
            >
              {copyStatus === 'success' ? <FiCheck size='1rem' /> : <FiCopy size='1rem' />}
              {copyStatus === 'success' ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          {copyStatus === 'success' && (
            <Typography variant='small' className='mb-2 text-green-700'>
              El mensaje se copió al portapapeles.
            </Typography>
          )}
          {copyStatus === 'error' && (
            <Typography variant='small' className='mb-2 text-red-700'>
              No se pudo copiar el mensaje al portapapeles.
            </Typography>
          )}
          <pre className='max-h-80 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-4 text-xs text-gray-900'>
            {errorDetails}
          </pre>
        </div>
      )}
    </NotificationModal>
  )
}

MassiveImportResultModal.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
      error: PropTypes.string
    })
  ),
  iconType: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired
}

MassiveImportResultModal.defaultProps = {
  errors: []
}
